<script lang="ts">
import {defineComponent} from 'vue';
import type {PropType} from 'vue';
import $ from 'jquery';
import {clamp} from '../js_utils.ts';
import type {WorkflowUsageSummary} from '../types.ts';

export type AllSpiceBillingActionsSettingsData = {
  actionsMinutesLimitModel: number,
  submitted: boolean,
  confirm: boolean,
  actionsMinutesRemaining: number,
  actionsMinutesFreeUsed: number,
  actionsMinutesPaidUsed: number,
  actionsMinutesPaidMax: number,
  actionsMinutesMinLimit: number,
  actionsMinutesFreeProgressLayoutPercent: number,
  actionsMinutesPaidProgressLayoutPercent: number,
  actionsMinutesPaidProgressLayoutVisible: boolean,
  billingLimitExpanded: boolean,
  usageBreakdownExpanded: boolean,
  actionsMinutesPriceDollars: number,
  actionsMinutesCurrentLimitPrice: string,
};

export default defineComponent({
  name: 'AllSpiceBillingActionsSettings',
  props: {
    link: {
      type: String,
      required: false,
      default: '',
    },
    csrf: {
      type: String,
      required: false,
      default: '',
    },
    billingPortalLink: {
      type: String,
      required: true,
      default: '',
    },
    billingReadOnly: {
      type: Boolean,
      required: true,
      default: false,
    },
    actionsEnabled: {
      type: Boolean,
      required: true,
      default: false,
    },
    actionsMinutesFree: {
      type: Number,
      required: true,
      default: 0,
    },
    actionsMinutesLimit: {
      type: Number,
      required: true,
      default: 0,
    },
    actionsMinutesUsed: {
      type: Number,
      required: true,
      default: 0,
    },
    actionsMinutesPrice: {
      type: Number,
      required: true,
      default: 0,
    },
    actionsMinutesCurrentPeriodEnd: {
      type: String,
      required: true,
      default: '',
    },
    usageByWorkflow: {
      type: Array as PropType<WorkflowUsageSummary[]>,
      required: false,
      default: () => [] as WorkflowUsageSummary[],
    },
  },
  data(): AllSpiceBillingActionsSettingsData {
    return {
      actionsMinutesLimitModel: 0,
      submitted: false,
      confirm: false,
      actionsMinutesRemaining: 0,
      actionsMinutesFreeUsed: 0,
      actionsMinutesPaidUsed: 0,
      actionsMinutesPaidMax: 0,
      actionsMinutesMinLimit: 0,
      actionsMinutesFreeProgressLayoutPercent: 0,
      actionsMinutesPaidProgressLayoutPercent: 0,
      actionsMinutesPaidProgressLayoutVisible: false,
      billingLimitExpanded: false,
      usageBreakdownExpanded: false,
      actionsMinutesPriceDollars: this.actionsMinutesPrice / 100,
      actionsMinutesCurrentLimitPrice: '0.00',
    };
  },
  mounted() {
    this.actionsMinutesLimitModel = this.actionsMinutesLimit;
    this.computeDollarLimit();
    this.actionsMinutesRemaining = clamp(this.actionsMinutesLimit - this.actionsMinutesUsed, 0, this.actionsMinutesLimit);
    this.actionsMinutesFreeUsed = clamp(this.actionsMinutesUsed, 0, this.actionsMinutesFree);
    this.actionsMinutesPaidUsed = clamp(this.actionsMinutesUsed - this.actionsMinutesFree, 0, this.actionsMinutesLimit - this.actionsMinutesFree);
    this.actionsMinutesPaidMax = this.actionsMinutesLimit - this.actionsMinutesFree;
    this.actionsMinutesMinLimit = Math.max(this.actionsMinutesUsed, this.actionsMinutesFree);
    this.actionsMinutesPaidProgressLayoutVisible = this.actionsMinutesLimit > this.actionsMinutesFree;

    if (this.actionsMinutesPaidProgressLayoutVisible) {
      // both progress bars visible
      this.actionsMinutesFreeProgressLayoutPercent = clamp(Math.floor(100 * this.actionsMinutesFree / this.actionsMinutesLimit), 25, 75);
      this.actionsMinutesPaidProgressLayoutPercent = 99 - this.actionsMinutesFreeProgressLayoutPercent;
    } else {
      this.actionsMinutesFreeProgressLayoutPercent = 99;
    }
  },
  methods: {
    /**
     * Given an updated minutes limit, compute in dollars
     */
    computeDollarLimit() {
      // clamp the limit input above the current usage, free tier
      const newLimit = Math.max(this.actionsMinutesLimitModel, this.actionsMinutesUsed, this.actionsMinutesFree);
      this.actionsMinutesCurrentLimitPrice = ((newLimit - this.actionsMinutesFree) * this.actionsMinutesPriceDollars).toFixed(3);
    },

    /**
     * Expand or hide a details section
     * @param {HTMLDivElement} elem
     */
    expandDetails(elem: HTMLDivElement) {
      this.billingLimitExpanded = !this.billingLimitExpanded;
      if (elem.style.maxHeight) {
        elem.style.maxHeight = '';
      } else {
        elem.style.maxHeight = `${elem.scrollHeight}px`;
      }
    },

    /**
     * Show the entitlement confirmation modal
     */
    showConfirmation() {
      // validate the entitlement value
      if (!(this.$refs.form as HTMLFormElement).reportValidity()) {
        return;
      }

      // check that there is a difference, open confirmation modal
      if (this.actionsMinutesLimitModel !== this.actionsMinutesLimit) {
        this.confirm = true;
      }
    },

    /**
     * Hide the entitlement confirmation modal
     */
    cancelConfirmation() {
      this.confirm = false;
    },

    /**
     * Show the loading indicator
     */
    onSubmit() {
      this.confirm = false;
      this.submitted = true;
      // prevent the browser from showing a confirmation dialog about unsaved changes
      $(window).off('beforeunload');
      (this.$refs.form as HTMLFormElement).submit();
    },
  },
});
</script>

<template>
  <form
    ref="form"
    class="ui form"
    :action="link"
    method="post"
  >
    <div
      class="form-contents"
      :style="{filter:submitted ? 'opacity(25%)' : 'opacity(100%)'}"
    >
      <input
        type="hidden"
        name="_csrf"
        :value="csrf"
      >
      <div
        v-show="confirm"
        class="confirmation-modal"
      >
        <div class="confirmation-contents">
          <h2>Confirmation</h2>
          <div v-if="actionsMinutesLimitModel !== actionsMinutesLimit">
            <div class="ui divider" />
            <div class="warning">
              Are you sure you want to change your actions minutes limit from {{ actionsMinutesLimit }} to {{ actionsMinutesLimitModel }}?
            </div>
            <div
              v-if="actionsMinutesLimitModel > actionsMinutesFree"
              class="details"
            >
              Changing your actions minutes limit to {{ actionsMinutesLimitModel }} may result in charges.
            </div>
            <div class="ui divider" />
          </div>
          <div class="bill-change">
            Visit the <a
              target="_blank"
              :href="billingPortalLink"
            >billing portal</a> to view the change to your bill.
          </div>
          <button
            ref="submitButton"
            class="ui primary button"
            :disabled="submitted"
            @click.stop.prevent="onSubmit"
          >
            Update
          </button>
          <button
            type="button"
            class="ui button"
            :disabled="submitted"
            @click.prevent="cancelConfirmation"
          >
            Cancel
          </button>
        </div>
      </div>
      <h4
        v-if="actionsEnabled"
        class="ui top attached header"
      >
        AllSpice Actions
      </h4>
      <div
        v-if="actionsEnabled && billingReadOnly"
        class="ui attached segment"
      >
        <progress
          id="actions-progress"
          style="width: 65%"
          :value="actionsMinutesUsed"
          :max="actionsMinutesLimit"
        />
        <label
          for="actions-progress"
          style="padding-left: 20px"
        >
          {{ actionsMinutesUsed }} of {{ actionsMinutesLimit }} actions minutes used
        </label>
        <div
          v-if="usageByWorkflow && usageByWorkflow.length > 0"
          class="tw:pt-2.5"
        >
          <a
            class="expand-billing-limit"
            @click="usageBreakdownExpanded = !usageBreakdownExpanded"
          >
            <svg
              v-if="usageBreakdownExpanded"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 16 16"
              width="16"
              height="16"
            >
              <path d="M12.78 5.22a.749.749 0 0 1 0 1.06l-4.25 4.25a.749.749 0 0 1-1.06 0L3.22 6.28a.749.749 0 1 1 1.06-1.06L8 8.939l3.72-3.719a.749.749 0 0 1 1.06 0Z" />
            </svg>
            <svg
              v-else
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 16 16"
              width="16"
              height="16"
            >
              <path d="M6.22 3.22a.75.75 0 0 1 1.06 0l4.25 4.25a.75.75 0 0 1 0 1.06l-4.25 4.25a.751.751 0 0 1-1.042-.018.751.751 0 0 1-.018-1.042L9.94 8 6.22 4.28a.75.75 0 0 1 0-1.06Z" />
            </svg>
            Usage by action
          </a>
          <div
            v-if="usageBreakdownExpanded"
            class="tw:py-2.5"
          >
            <table class="ui very basic table tw:text-xs">
              <thead>
                <tr>
                  <th>
                    Action
                  </th>
                  <th class="right aligned">
                    Runs
                  </th>
                  <th class="right aligned">
                    Minutes
                  </th>
                  <th class="right aligned">
                    % of Total
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="workflow in usageByWorkflow"
                  :key="workflow.workflow_id"
                >
                  <td>
                    {{ workflow.display_name }}
                  </td>
                  <td class="right aligned">
                    {{ workflow.run_count }}
                  </td>
                  <td class="right aligned">
                    {{ workflow.billable_minutes }}
                  </td>
                  <td class="right aligned">
                    {{ workflow.percent_of_total.toFixed(1) }}%
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
        <div style="padding-top: 15px">
          For Actions billing related questions, please contact <a
            target="_blank"
            rel="noopener noreferrer"
            href="mailto:support@allspice.io"
          >support@allspice.io</a>.
        </div>
      </div>
      <div
        v-if="actionsEnabled && !billingReadOnly"
        class="ui attached segment"
      >
        <div class="progress-title">
          Minutes Usage
          <span
            class="info-icon"
            :data-tooltip-content="'The first ' + actionsMinutesFree + ' min are free, then $' + actionsMinutesPriceDollars + '/min.'"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 16 16"
              class="svg octicon-info"
              width="16"
              height="16"
              aria-hidden="true"
            >
              <path d="M0 8a8 8 0 1 1 16 0A8 8 0 0 1 0 8Zm8-6.5a6.5 6.5 0 1 0 0 13 6.5 6.5 0 0 0 0-13ZM6.5 7.75A.75.75 0 0 1 7.25 7h1a.75.75 0 0 1 .75.75v2.75h.25a.75.75 0 0 1 0 1.5h-2a.75.75 0 0 1 0-1.5h.25v-2h-.25a.75.75 0 0 1-.75-.75ZM8 6a1 1 0 1 1 0-2 1 1 0 0 1 0 2Z" />
            </svg>
          </span>
        </div>
        <div class="progress-sub-title">
          Set spend limit <b>{{ actionsMinutesLimit }} min / month</b>
        </div>
        <div class="multi-progress">
          <div
            class="actions-progress-bar"
            :style="{width:actionsMinutesFreeProgressLayoutPercent + '%'}"
          >
            <progress
              id="actions-progress1"
              class="progress-free"
              style="width: 100%"
              :value="actionsMinutesFreeUsed"
              :max="actionsMinutesFree"
            />
            <label
              for="actions-progress1"
              class="actions-progress-label"
            >
              {{ actionsMinutesFreeUsed }}/{{ actionsMinutesFree }} free minutes used
            </label>
          </div>
          <div
            v-if="actionsMinutesPaidProgressLayoutVisible"
            class="actions-progress-bar"
            :style="{width:actionsMinutesPaidProgressLayoutPercent + '%'}"
          >
            <progress
              id="actions-progress2"
              class="progress-paid"
              style="width: 100%"
              :value="actionsMinutesPaidUsed"
              :max="actionsMinutesPaidMax"
            />
            <label
              for="actions-progress2"
              class="actions-progress-label"
            >
              {{ actionsMinutesPaidUsed }}/{{ actionsMinutesPaidMax }} billable minutes used
            </label>
          </div>
        </div>
        <div
          v-if="usageByWorkflow && usageByWorkflow.length > 0"
          class="tw:pt-2.5"
        >
          <a
            class="expand-billing-limit"
            @click="usageBreakdownExpanded = !usageBreakdownExpanded"
          >
            <svg
              v-if="usageBreakdownExpanded"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 16 16"
              width="16"
              height="16"
            >
              <path d="M12.78 5.22a.749.749 0 0 1 0 1.06l-4.25 4.25a.749.749 0 0 1-1.06 0L3.22 6.28a.749.749 0 1 1 1.06-1.06L8 8.939l3.72-3.719a.749.749 0 0 1 1.06 0Z" />
            </svg>
            <svg
              v-else
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 16 16"
              width="16"
              height="16"
            >
              <path d="M6.22 3.22a.75.75 0 0 1 1.06 0l4.25 4.25a.75.75 0 0 1 0 1.06l-4.25 4.25a.751.751 0 0 1-1.042-.018.751.751 0 0 1-.018-1.042L9.94 8 6.22 4.28a.75.75 0 0 1 0-1.06Z" />
            </svg>
            Usage by action
          </a>
          <div
            v-if="usageBreakdownExpanded"
            class="tw:py-2.5"
          >
            <table class="ui very basic table tw:text-xs">
              <thead>
                <tr>
                  <th>
                    Action
                  </th>
                  <th class="right aligned">
                    Runs
                  </th>
                  <th class="right aligned">
                    Minutes
                  </th>
                  <th class="right aligned">
                    % of Total
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="workflow in usageByWorkflow"
                  :key="workflow.workflow_id"
                >
                  <td>
                    {{ workflow.display_name }}
                  </td>
                  <td class="right aligned">
                    {{ workflow.run_count }}
                  </td>
                  <td class="right aligned">
                    {{ workflow.billable_minutes }}
                  </td>
                  <td class="right aligned">
                    {{ workflow.percent_of_total.toFixed(1) }}%
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
        <a
          class="expand-billing-limit"
          @click="expandDetails($refs.setBillingLimit as HTMLDivElement)"
        >
          <svg
            v-if="billingLimitExpanded"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 16 16"
            width="16"
            height="16"
          >
            <path d="M12.78 5.22a.749.749 0 0 1 0 1.06l-4.25 4.25a.749.749 0 0 1-1.06 0L3.22 6.28a.749.749 0 1 1 1.06-1.06L8 8.939l3.72-3.719a.749.749 0 0 1 1.06 0Z" />
          </svg>
          <svg
            v-else
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 16 16"
            width="16"
            height="16"
          >
            <path d="M6.22 3.22a.75.75 0 0 1 1.06 0l4.25 4.25a.75.75 0 0 1 0 1.06l-4.25 4.25a.751.751 0 0 1-1.042-.018.751.751 0 0 1-.018-1.042L9.94 8 6.22 4.28a.75.75 0 0 1 0-1.06Z" />
          </svg>
          Set up a monthly limit
        </a>
        <div
          ref="setBillingLimit"
          class="collapsed-billing-limit"
        >
          <div class="loading-contents">
            <div class="billing-limit-contents">
              <span>
                You can setup a monthly limit based on the maximum number of minutes.<br>
                The changes made here will only be saved after after clicking 'Update Org Billing' below.
              </span>
              <div class="billing-limit-form">
                <div class="inline field">
                  <input
                    id="actions_limit"
                    v-model="actionsMinutesLimitModel"
                    name="actions_limit"
                    type="number"
                    :min="actionsMinutesMinLimit"
                    required
                    :disabled="submitted"
                    @input="computeDollarLimit"
                  >
                  <label
                    style="font-size: 14px"
                    for="actions_limit"
                  >minutes</label>
                </div>
              </div>
              <div class="billing-limit-form-details">
                Billable limit: ${{ actionsMinutesCurrentLimitPrice }} (${{ (actionsMinutesPriceDollars).toFixed(3) }} per minute)
              </div>
            </div>
          </div>
        </div>
        <div class="billing-limit-footer">
          <div style="padding-top: 15px">
            For Actions billing related questions, please contact <a
              target="_blank"
              rel="noopener noreferrer"
              href="mailto:support@allspice.io"
            >support@allspice.io</a>.
          </div>
          <div class="billable-spend">
            <div>
              Total billable spend: <b>${{ (actionsMinutesPaidUsed * actionsMinutesPriceDollars).toFixed(3) }} ({{ actionsMinutesUsed }}min)</b>
            </div>
            <div class="billable-spend-invoice">
              Cycle resets
              <relative-time :datetime="actionsMinutesCurrentPeriodEnd">
                {{ actionsMinutesCurrentPeriodEnd }}
              </relative-time>
            </div>
          </div>
        </div>
        <div style="padding-top: 20px; padding-bottom: 20px">
          <button
            v-if="!billingReadOnly"
            type="button"
            class="ui primary button"
            :disabled="confirm || submitted"
            @click.prevent="showConfirmation"
          >
            Update Actions Billing
          </button>
        </div>
      </div>
    </div>
  </form>
</template>

<style scoped>
.confirmation-modal {
  z-index: 1000;
  border: 1px solid var(--color-secondary);
  border-radius: var(--border-radius);
  background-color: var(--color-box-body);
  box-shadow: 1.5rem 2rem 3rem var(--color-shadow);
  color: var(--color-text);
  overflow: auto;
  position: absolute;
  width: 100%;
}
.confirmation-contents {
  padding: 30px;
}
.warning {
  font-size: 15px;
  display: block;
  padding-bottom: 10px;
}
.details {
  color: var(--color-text-light-2);
  display: block;
  padding-bottom: 20px;
}
.bill-change {
  padding-top: 35px;
  padding-bottom: 35px;
  font-size: 20px;
  font-weight: var(--font-weight-semibold);
}
.progress-title {
  font-size: 14px;
  padding-bottom: 10px;
  font-weight: var(--font-weight-semibold);
  display: inline-block;
}
.progress-sub-title {
  display: block;
  float: right;
  padding: 5px;
}
.multi-progress {
  padding-top: 10px;
}
.info-icon {
  padding-left: 10px;
  white-space: nowrap;
}
.progress-free::-moz-progress-bar {
  background: var(--color-allspice-blue-lighter);
}
.progress-free::-webkit-progress-value {
  background: var(--color-allspice-blue-lighter);
}
.progress-free {
  color: var(--color-allspice-blue-lighter);
}
.progress-paid::-moz-progress-bar {
  background: var(--color-allspice-blue-mid);
}
.progress-paid::-webkit-progress-value {
  background: var(--color-allspice-blue-mid);
}
.progress-paid {
  color: var(--color-allspice-blue-mid);
}
.actions-progress-bar {
  display: inline-block;
  margin-right: 4px;
}
.actions-progress-label {
  padding-top: 5px;
  float: right;
  color: var(--color-text-light-2);
  font-size: 12px;
}
.expand-billing-limit {
  text-decoration: none;
  color: var(--color-text);
  font-weight: var(--font-weight-semibold);
  display: flex;
  align-items: center;
  padding-top: 20px;
}
.expand-billing-limit svg {
  margin-right: 5px;
}
.collapsed-billing-limit {
  padding: 0 18px;
  max-height: 0;
  overflow: hidden;
  transition: max-height 0.2s ease-out;
}
.billing-limit-contents {
  padding: 10px;
}
.billing-limit-form {
  padding-top: 15px;
  display: flex;
}
.billing-limit-form-details {
  padding-top: 10px;
  color: var(--color-text-light-2);
}
.billing-limit-footer {
  padding-top: 25px;
  display: flex;
}
.billable-spend {
  margin-left: auto;
}
.billable-spend-invoice {
  color: var(--color-text-light-2);
}
</style>
